import { useState, useCallback, memo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { Box, Button, H6, SelectChangeEvent, Snackbar } from '@parspec/pixel';

import { QuoteCoverPagePreference } from './components/QuoteCoverPagePreference';
import { QuoteHeaderFooterPreference } from './components/QuoteHeaderFooterPreference';
import { PricingLeadTime } from './components/PricingLeadTime';
import { GeneralTermsAndConditions } from './components/GeneralTermsAndConditions';
import { ManufacturerTermsAndConditions } from './components/ManufacturerTermsAndConditions';
import { Attachments } from './components/Attachment/Attachments';
import {
    OPENING_NOTES_TEXT,
    PRE_FILL_ADDRESSED_TO,
    PRE_FILL_BID_DATE,
    PRE_FILL_EXPIRATION_DATE,
    PRE_FILL_HEADER_LOGO,
    PRE_FILL_JOB_NAME,
    PRE_FILL_QUOTE_NUMBER,
    QUOTE_COVER_PAGE,
    QUOTE_HEADER_FOOTER,
    QUOTE_PRICE_LEAD_TIME,
    SUMMARIZED_TOTAL
} from './utils/constants';
import { useLocationDetails } from '../context';
import { MultipleBranchesModal } from '../../shared/components/MultipleBranchesModal/MultipleBranchesModal';
import { SaveButton } from '../../shared/components/SaveButton';
import { useEditQuoteSettingsMutation, useBranchDetailsQuery } from '../../queries';
import { EDIT_SETTINGS_SUCCESS_MSG } from '../../shared/constants';
import { useParspecNavigation } from 'src/app/Context';
import { IQuoteAttachmentDocumentsPreset } from '../../queries/apiTypes';
import { attachmentDocumentArrFormatter } from '../../utils/utils';

const QuoteTemplate = () => {
    const { currentLocationId, quoteTemplate, setQuoteTemplate, branchLocationsRef } = useLocationDetails();

    const location = useLocation();
    const currentTab = location.pathname.split('/').filter(Boolean).pop();
    const { pNavigate, hasUnsavedChangesRef } = useParspecNavigation()

    const { data: companyData } = useBranchDetailsQuery(Number(currentLocationId), { enabled: Boolean(currentLocationId && Number(currentLocationId) !== 0) });

    const { mutateAsync: editQuoteData, isLoading } = useEditQuoteSettingsMutation();

    useEffect(() => {
        const currentBranchLocation = branchLocationsRef.current;
        return () => {
            if (currentBranchLocation?.quoteTemplate) {
                setQuoteTemplate(currentBranchLocation.quoteTemplate)
            }
        }
    }, [branchLocationsRef, setQuoteTemplate])


    // Ask hrishikesh about Header and footer.
    const changeHandler = (event: SelectChangeEvent<unknown>, presetName: string, field_name?: string) => {
        hasUnsavedChangesRef.current = true;
        const fieldName = field_name ? field_name : event.target?.name;
        const checkedField = event.target as HTMLInputElement;
        if (presetName === 'attachments') {
            // for toggling attachment switch
            setQuoteTemplate(() => ({ ...quoteTemplate, [presetName]: { ...quoteTemplate[presetName], [fieldName]: checkedField.checked } }));
            return;
        }

        const isOpeningNotesFromCoverPage = presetName === QUOTE_COVER_PAGE && fieldName === OPENING_NOTES_TEXT;
        const isOptionNotesFromPriceLeadTime = presetName === 'quote_price_lead_time_option' && fieldName === 'option_notes_text';
        const isClosingNotesFromGeneralTnC = presetName === 'quote_general_tnc' && fieldName === 'closing_notes_text';

        const isSumarrisedTotal = presetName === QUOTE_PRICE_LEAD_TIME && fieldName === SUMMARIZED_TOTAL;

        const isTextFromQuoteHeaderFooter =
            presetName === QUOTE_HEADER_FOOTER &&
            (fieldName === PRE_FILL_JOB_NAME ||
                fieldName === PRE_FILL_QUOTE_NUMBER ||
                fieldName === PRE_FILL_BID_DATE ||
                fieldName === PRE_FILL_EXPIRATION_DATE ||
                fieldName === PRE_FILL_HEADER_LOGO ||
                fieldName === PRE_FILL_ADDRESSED_TO);

        const isPreparedByLogo = presetName === 'quote_cover_page' && fieldName === 'pre_fill_preparedby_logo';
        if (isOpeningNotesFromCoverPage || isClosingNotesFromGeneralTnC || isOptionNotesFromPriceLeadTime) {
            const value = event.target?.value;

            return setQuoteTemplate(() => ({
                ...quoteTemplate,
                [presetName]: { ...quoteTemplate[presetName], [fieldName]: { ...quoteTemplate[presetName][fieldName], value: value } }
            }));
        }

        let fieldValue: string | boolean;
        if (isSumarrisedTotal || isTextFromQuoteHeaderFooter || isPreparedByLogo || (presetName === QUOTE_COVER_PAGE && fieldName === PRE_FILL_ADDRESSED_TO)) {
            fieldValue = event.target?.value as string;
        } else {
            fieldValue = checkedField.checked;
        }

        setQuoteTemplate(() => ({ ...quoteTemplate, [presetName]: { ...quoteTemplate[presetName], [fieldName]: fieldValue } }));
    };

    const textEditorOnChangeHandler = useCallback(
        (html: string, presetName: string, fieldName: string) => {
            if (companyData?.data?.quote) {
                if (companyData?.data?.quote?.[presetName]?.[fieldName] === html) {
                    return;
                }
                hasUnsavedChangesRef.current = true;
                setQuoteTemplate((prev) => ({ ...prev, [presetName]: { ...prev[presetName], [fieldName]: html } }));
            }
        },
        [companyData?.data?.quote, hasUnsavedChangesRef, setQuoteTemplate]
    );

    const handleColorAndFontTypeChange = (value: string, presetName: string, fieldName: string, fieldType: string) => {
        hasUnsavedChangesRef.current = true;
        if (fieldType === 'type') {
            setQuoteTemplate(() => ({
                ...quoteTemplate,
                [presetName]: { ...quoteTemplate[presetName], [fieldName]: { ...quoteTemplate[presetName][fieldName], font: { ...quoteTemplate[presetName][fieldName]?.font, type: value } } }
            }));
        } else if (fieldType === 'color') {
            setQuoteTemplate(() => ({
                ...quoteTemplate,
                [presetName]: { ...quoteTemplate[presetName], [fieldName]: { ...quoteTemplate[presetName][fieldName], font: { ...quoteTemplate[presetName][fieldName]?.font, color: value } } }
            }));
        }
    };

    const handleAttachments = (files: IQuoteAttachmentDocumentsPreset[]) => {
        hasUnsavedChangesRef.current = true;
        setQuoteTemplate((prev) => ({ ...prev, attachments: { ...prev.attachments, documents: files } }));
    };

    const handleLockChange = (value: boolean) => {
        hasUnsavedChangesRef.current = true;
        setQuoteTemplate(() => ({ ...quoteTemplate, 'quote_general_tnc': { ...quoteTemplate['quote_general_tnc'], 'is_general_tnc_locked': value } }));
    };

    const resetDataHandler = () => {
        pNavigate(`/v2/settings/company-locations`);
    };

    const [isMultipleBranchesModalOpen, setIsMultipleBranchesModalOpen] = useState(false);
    const [snackbarData, setSnackbarData] = useState('');

    const payloadFormatter = () => {
        const data = { ...quoteTemplate, attachments: { ...quoteTemplate?.attachments, documents: attachmentDocumentArrFormatter(quoteTemplate?.attachments?.documents) } }
        return data

    }

    const onOptionSelection = async (type: string) => {
        switch (type) {
            case 'single':

                const quotePayload = payloadFormatter()
                const payload = {
                    apply_settings_to_company_ids: [currentLocationId], payload: quotePayload
                }
                try {
                    hasUnsavedChangesRef.current = false;
                    await editQuoteData({ data: payload });
                    setSnackbarData(() => EDIT_SETTINGS_SUCCESS_MSG)
                } catch (err: any) {
                    setSnackbarData(() => err?.response?.data?.message ? err?.response?.data?.message : 'Something went wrong, please try again');
                }

                break;

            case 'multiple':
                setIsMultipleBranchesModalOpen(() => true);
                break;
        }
    };
    const onColumnSelection = async (selectedBranchIdsArr: number[]) => {
        const quotePayload = payloadFormatter()
        const payload = {
            apply_settings_to_company_ids: selectedBranchIdsArr, payload: quotePayload
        }
        try {
            hasUnsavedChangesRef.current = false;
            await editQuoteData({ data: payload });
            setSnackbarData(() => EDIT_SETTINGS_SUCCESS_MSG)
        } catch (err: any) {
            setSnackbarData(() => err?.response?.data?.message ? err?.response?.data?.message : 'Something went wrong, please try again');
        }
        setIsMultipleBranchesModalOpen(() => false);
    };

    const handleCloseMultipleBranchesModal = () => {
        setIsMultipleBranchesModalOpen(() => false);
    }

    return (
        <Box bgcolor="primary.contrastText">
            <Box display="flex">
                <Box>
                    <H6>Quote Template Preferences</H6>
                </Box>
                <Box ml="auto" display="flex">
                    <Box display="flex">
                        <Box mr={2}>
                            <Button variant="outlined" color="secondary" onClick={resetDataHandler}>
                                Cancel
                            </Button>
                        </Box>
                        <Box>
                            <SaveButton onOptionSelection={onOptionSelection} isLoading={isLoading} />

                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box>
                <Box mt={6}>
                    <QuoteHeaderFooterPreference changeHandler={changeHandler} templateState={quoteTemplate} />
                    <QuoteCoverPagePreference changeHandler={changeHandler} templateState={quoteTemplate} handleColorAndFontTypeChange={handleColorAndFontTypeChange} />
                    <PricingLeadTime changeHandler={changeHandler} templateState={quoteTemplate} handleColorAndFontTypeChange={handleColorAndFontTypeChange} />
                    <GeneralTermsAndConditions
                        changeHandler={changeHandler}
                        templateState={quoteTemplate}
                        handleColorAndFontTypeChange={handleColorAndFontTypeChange}
                        onLockToggle={handleLockChange}
                        onChangeHtml={textEditorOnChangeHandler}
                    />
                    <ManufacturerTermsAndConditions changeHandler={changeHandler} templateState={quoteTemplate} />
                    <Attachments changeHandler={changeHandler} handleAttachments={handleAttachments} templateState={quoteTemplate} />
                </Box>
            </Box>

            <MultipleBranchesModal
                isOpen={isMultipleBranchesModalOpen}
                onClose={handleCloseMultipleBranchesModal}
                currentTab={currentTab}
                onColumnSelection={onColumnSelection}
                isLoading={isLoading}
            />

            {<Snackbar open={snackbarData?.length > 0} message={snackbarData} onClose={() => setSnackbarData(() => '')} />}

        </Box>
    );
};

export default memo(QuoteTemplate);
